/**
 * RIWAYAT LAPORAN SHIFT - PENJAGA LOKET
 *
 * Menampilkan riwayat laporan shift yang sudah dibuat
 * Dengan fitur upload, edit, delete (berdasarkan permission settings)
 */

import React, { useState, useEffect } from 'react';
import { Clock, Calendar, FileText, Filter, Search, Eye, X, Upload, Edit2, Trash2, RefreshCw, CheckCircle, AlertCircle, Share2, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const RiwayatLoketPage = ({ currentUser, api }) => {
  const user = currentUser;
  const [laporanList, setLaporanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLaporan, setSelectedLaporan] = useState(null);
  const [editingLaporan, setEditingLaporan] = useState(null); // State untuk laporan yang sedang diedit
  const [showEditModal, setShowEditModal] = useState(false); // Show/hide modal edit
  const [providers, setProviders] = useState([]); // List providers untuk dropdown
  const [bankTransaksi, setBankTransaksi] = useState({ BSI: {}, MANDIRI: {} }); // Editable transactions
  const [activeEditTab, setActiveEditTab] = useState('BSI'); // Active tab for edit modal
  const [filterDate, setFilterDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    allow_edit_laporan: false,
    allow_delete_laporan: false
  });

  // Load settings dan riwayat laporan
  useEffect(() => {
    loadSettings();
    loadRiwayatLaporan();
    loadProviders();
  }, [user?.loket_id, user?.role]);

  // Load providers
  const loadProviders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProviders(data.filter(p => p.is_active));
      }
    } catch (err) {
      console.error('Gagal load providers:', err);
    }
  };

  // Helper: Convert bank_summaries to bankTransaksi format
  const convertToBankTransaksi = (laporan) => {
    const result = { BSI: {}, MANDIRI: {} };

    // Initialize with all providers
    providers.forEach(p => {
      result.BSI[p.id] = {
        provider_id: p.id,
        provider_name: p.nama,
        kategori: p.kategori,
        biaya_admin: p.biaya_admin || 0,
        tunai: { jumlah: 0, tagihan: 0, admin_fee: 0, checked: false },
        non_tunai: { jumlah: 0, tagihan: 0, admin_fee: 0, checked: false }
      };
      result.MANDIRI[p.id] = {
        provider_id: p.id,
        provider_name: p.nama,
        kategori: p.kategori,
        biaya_admin: p.biaya_admin || 0,
        tunai: { jumlah: 0, tagihan: 0, admin_fee: 0, checked: false },
        non_tunai: { jumlah: 0, tagihan: 0, admin_fee: 0, checked: false }
      };
    });

    // Fill with existing transactions
    if (laporan.bank_summaries) {
      laporan.bank_summaries.forEach(bank => {
        const bankId = bank.bank_id; // 'BSI' or 'MANDIRI'

        // Ensure bankId is valid
        if (!result[bankId]) {
          console.warn('Unknown bank_id:', bankId);
          return;
        }

        if (bank.transaksi_list) {
          bank.transaksi_list.forEach(trx => {
            const providerId = trx.provider_id;

            // Check if provider exists in our initialized structure
            if (result[bankId] && result[bankId][providerId]) {
              const tipe = trx.tipe_transaksi === 'TUNAI' ? 'tunai' : 'non_tunai';
              result[bankId][providerId][tipe] = {
                jumlah: trx.jumlah_trx || 0,
                tagihan: trx.total_nominal || 0,
                admin_fee: trx.total_admin_fee || 0,
                checked: true // Enable if has data
              };
            } else {
              console.warn('Provider not found:', bankId, providerId);
            }
          });
        }
      });
    }

    return result;
  };

  // Update transaksi (helper function)
  const updateTransaksi = (bankId, providerId, tipe, field, value) => {
    setBankTransaksi(prev => {
      const bankData = prev[bankId] || {};
      const providerData = bankData[providerId] || {};

      const numValue = field === 'jumlah' ? parseInt(value) || 0 : parseFloat(value) || 0;
      const biayaAdmin = providerData.biaya_admin || 0;

      const newTipeData = {
        ...providerData[tipe],
        [field]: numValue
      };

      // Auto-calculate admin_fee when jumlah changes
      if (field === 'jumlah') {
        newTipeData.admin_fee = numValue * biayaAdmin;
      }

      return {
        ...prev,
        [bankId]: {
          ...bankData,
          [providerId]: {
            ...providerData,
            [tipe]: newTipeData
          }
        }
      };
    });
  };

  // Toggle transaction type checkbox
  const toggleTransactionType = (bankId, providerId, tipe) => {
    setBankTransaksi(prev => ({
      ...prev,
      [bankId]: {
        ...prev[bankId],
        [providerId]: {
          ...prev[bankId][providerId],
          [tipe]: {
            ...prev[bankId][providerId][tipe],
            checked: !prev[bankId][providerId][tipe].checked,
            jumlah: !prev[bankId][providerId][tipe].checked ? 0 : prev[bankId][providerId][tipe].jumlah,
            tagihan: !prev[bankId][providerId][tipe].checked ? 0 : prev[bankId][providerId][tipe].tagihan,
            admin_fee: !prev[bankId][providerId][tipe].checked ? 0 : prev[bankId][providerId][tipe].admin_fee
          }
        }
      }
    }));
  };

  // Convert bankTransaksi back to transaksi_bsi/transaksi_mandiri format
  const convertToSubmitFormat = () => {
    const transaksi_bsi = [];
    const transaksi_mandiri = [];

    Object.entries(bankTransaksi.BSI).forEach(([providerId, data]) => {
      if (data.tunai.checked && data.tunai.jumlah > 0) {
        transaksi_bsi.push({
          provider_id: providerId,
          jumlah_transaksi: data.tunai.jumlah,
          total_tagihan: data.tunai.tagihan,
          tipe_transaksi: 'TUNAI'
        });
      }
      if (data.non_tunai.checked && data.non_tunai.jumlah > 0) {
        transaksi_bsi.push({
          provider_id: providerId,
          jumlah_transaksi: data.non_tunai.jumlah,
          total_tagihan: data.non_tunai.tagihan,
          tipe_transaksi: 'NON-TUNAI'
        });
      }
    });

    Object.entries(bankTransaksi.MANDIRI).forEach(([providerId, data]) => {
      if (data.tunai.checked && data.tunai.jumlah > 0) {
        transaksi_mandiri.push({
          provider_id: providerId,
          jumlah_transaksi: data.tunai.jumlah,
          total_tagihan: data.tunai.tagihan,
          tipe_transaksi: 'TUNAI'
        });
      }
      if (data.non_tunai.checked && data.non_tunai.jumlah > 0) {
        transaksi_mandiri.push({
          provider_id: providerId,
          jumlah_transaksi: data.non_tunai.jumlah,
          total_tagihan: data.non_tunai.tagihan,
          tipe_transaksi: 'NON-TUNAI'
        });
      }
    });

    return { transaksi_bsi, transaksi_mandiri };
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/settings/laporan-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // SUPERKASIR can only VIEW, not edit/delete
        if (user?.role === 'SUPERKASIR') {
          setSettings({
            ...data,
            allow_edit_laporan: false,
            allow_delete_laporan: false
          });
        } else {
          setSettings(data);
        }
      }
    } catch (err) {
      console.error('Gagal load settings:', err);
    }
  };

  const loadRiwayatLaporan = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');

      // OWNER, MANAGER, SUPERKASIR can see ALL laporan, others only their loket
      let url;
      if (user?.role === 'SUPERKASIR' || user?.role === 'OWNER' || user?.role === 'MANAGER') {
        // OWNER, MANAGER, SUPERKASIR: Show all laporan without loket filter
        url = filterDate
          ? `${api}/api/laporan-shift?tanggal=${filterDate}`
          : `${api}/api/laporan-shift`;
      } else {
        // Others: Filter by their loket_id
        url = filterDate
          ? `${api}/api/laporan-shift?loket_id=${user?.loket_id}&tanggal=${filterDate}`
          : `${api}/api/laporan-shift?loket_id=${user?.loket_id}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Gagal memuat riwayat laporan');
      }

      const data = await response.json();
      setLaporanList(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Upload bukti transaksi (MULTIPLE FILES SUPPORT)
  const handleUploadBukti = async (files, laporanId) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();

      // Append all files to FormData
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${api}/api/laporan-shift/${laporanId}/upload-bukti`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('Gagal upload bukti');

      const result = await response.json();
      setSuccess(`Berhasil upload ${result.files?.length || files.length} bukti transaksi!`);
      loadRiwayatLaporan();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Delete bukti transaksi
  const handleDeleteBukti = async (laporanId, buktiUrl) => {
    if (!confirm('Yakin ingin menghapus bukti transaksi ini?')) {
      return;
    }

    setUploading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${api}/api/laporan-shift/${laporanId}/bukti?bukti_url=${encodeURIComponent(buktiUrl)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Gagal menghapus bukti');
      }

      setSuccess('Bukti berhasil dihapus!');
      // Refresh selected laporan data
      const updatedLaporan = await fetch(`${api}/api/laporan-shift/${laporanId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (updatedLaporan.ok) {
        const data = await updatedLaporan.json();
        setSelectedLaporan(data);
      }
      loadRiwayatLaporan();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Edit laporan - Buka modal dengan form edit
  const handleEdit = (laporan) => {
    setEditingLaporan(laporan);
    setActiveEditTab('BSI'); // Reset to BSI tab
    // Convert existing transactions to editable format
    setBankTransaksi(convertToBankTransaksi(laporan));
    setShowEditModal(true);
    setError('');
  };

  // Handle submit update laporan
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Convert bankTransaksi to submit format
      const { transaksi_bsi, transaksi_mandiri } = convertToSubmitFormat();

      // Siapkan data yang akan dikirim - sesuai struktur CreateLaporanShiftRequest
      const updateData = {
        loket_id: editingLaporan.loket_id || user?.loket_id,
        tanggal: editingLaporan.tanggal,
        shift_period: editingLaporan.shift_period,
        saldo_awal_bsi: editingLaporan.saldo_awal_bsi,
        saldo_awal_mandiri: editingLaporan.saldo_awal_mandiri,
        // Kirim transaksi yang sudah diedit
        transaksi_bsi,
        transaksi_mandiri
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/laporan-shift/${editingLaporan.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Gagal mengupdate laporan');
      }

      setSuccess('Laporan berhasil diupdate!');
      setShowEditModal(false);
      setEditingLaporan(null);
      setBankTransaksi({ BSI: {}, MANDIRI: {} }); // Reset transactions
      loadRiwayatLaporan();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete laporan
  const handleDeleteLaporan = async (laporanId) => {
    if (!confirm('Yakin ingin menghapus laporan ini? Tindakan tidak dapat dibatalkan!')) {
      return;
    }

    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/laporan-shift/${laporanId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Gagal menghapus laporan');
      }

      setSuccess('Laporan berhasil dihapus!');
      loadRiwayatLaporan();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatRupiah = (num) => {
    if (!num && num !== 0) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num);
  };

  // Generate WhatsApp share message for laporan
  const shareToWhatsApp = (laporan) => {
    try {
      // Helper untuk format rupiah WA-friendly (tanpa simbol aneh)
      const formatRupiahWA = (num) => {
        if (!num && num !== 0) return 'Rp 0';
        return 'Rp ' + new Intl.NumberFormat('id-ID', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(num);
      };

      // Helper format tanggal WA-friendly
      const formatTanggalWA = (dateString) => {
        if (!dateString) return '-';
        const date = parseDate(dateString);
        if (!date) return '-';
        return date.toLocaleDateString('id-ID', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(',', '').replace(/\./g, ':');
      };

      // Build message dengan validasi data
      let message = `📊 *LAPORAN SHIFT PPOB*\n`;
      message += `───────────────────\n\n`;

      message += `🔹 No. Laporan: ${laporan.nomor_laporan || '-'}\n`;
      message += `🔹 Loket: ${laporan.loket_name || laporan.loket_nama || '-'}\n`;
      message += `🔹 Shift: ${laporan.shift_period || '-'}\n`;
      message += `🔹 Tanggal: ${formatTanggalWA(laporan.created_at)}\n\n`;

      message += `💰 *RINGKASAN TRANSAKSI*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n`;
      message += `Total Transaksi: ${laporan.total_transaksi || 0} trx\n`;
      message += `Total Tagihan: ${formatRupiahWA(laporan.total_tagihan || 0)}\n`;
      message += `Total Admin Fee: ${formatRupiahWA(laporan.total_admin_fee || 0)}\n`;
      message += `💵 Total OMZET: ${formatRupiahWA(laporan.total_omzet || 0)}\n`;

      // Detail per bank jika ada
      if (laporan.bank_summaries && laporan.bank_summaries.length > 0) {
        message += `\n📦 *DETAIL PER BANK*\n`;
        message += `━━━━━━━━━━━━━━━━━━━━`;

        laporan.bank_summaries.forEach((bank) => {
          message += `\n\n🏦 ${bank.bank_nama || bank.bank_id}\n`;
          message += `Saldo Awal: ${formatRupiahWA(bank.saldo_awal || 0)}\n`;
          message += `Total Trx: ${bank.total_transaksi || 0}\n`;
          message += `Total Tagihan: ${formatRupiahWA(bank.total_tagihan || 0)}\n`;
          message += `Total Admin: ${formatRupiahWA(bank.total_admin_fee || 0)}\n`;
          message += `💵 Saldo Akhir: ${formatRupiahWA(bank.saldo_akhir || 0)}`;
        });
      }

      // Uang kecil jika ada
      if (laporan.uang_kecil !== undefined && laporan.uang_kecil !== null) {
        message += `\n\n💰 Uang Kecil: ${formatRupiahWA(laporan.uang_kecil)}`;
      }

      message += `\n\n───────────────────\n`;
      message += `📱 Generated by SIP GELIS V5.0`;

      // Encode dan buka WhatsApp
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;

      // Debug log (bisa dihapus nanti)
      console.log('WhatsApp message length:', message.length);
      console.log('WhatsApp URL length:', whatsappUrl.length);

      // Open di new tab
      window.open(whatsappUrl, '_blank');

    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      setError('Gagal membuka WhatsApp. Silakan coba lagi.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    // Handle extended JSON: {"$date": "2026-08-08T07:32:44.326Z"}
    if (typeof dateString === 'object' && dateString.$date) {
      return new Date(dateString.$date);
    }
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = parseDate(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Nama hari dalam Bahasa Indonesia
  const getNamaHari = (dateString) => {
    if (!dateString) return '-';
    const date = parseDate(dateString);
    if (!date) return '-';
    const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    return days[date.getDay()];
  };

  // Filter laporan berdasarkan search term
  const filteredLaporan = laporanList.filter(lap => {
    const searchLower = searchTerm.toLowerCase();
    return (
      lap.nomor_laporan?.toLowerCase().includes(searchLower) ||
      lap.shift_period?.toLowerCase().includes(searchLower) ||
      lap.loket_name?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const ITEMS_PER_PAGE = 9;
  const totalPages = Math.max(1, Math.ceil(filteredLaporan.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
  const paginatedLaporan = filteredLaporan.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 if filters change and current page is out of range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Build page numbers to display (with ellipsis)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, safePage - 1);
      let end = Math.min(totalPages - 1, safePage + 1);
      if (safePage <= 3) {
        end = 4;
      } else if (safePage >= totalPages - 2) {
        start = totalPages - 3;
      }
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  // View detail laporan
  const viewDetail = (laporan) => {
    setSelectedLaporan(laporan);
  };

  const closeDetail = () => {
    setSelectedLaporan(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Clock size={40} className="animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Memuat riwayat laporan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Riwayat Laporan Shift</h1>
        <p className="text-gray-600">Daftar laporan shift yang sudah dibuat</p>
      </div>

      {/* SUPERKASIR/OWNER/MANAGER Notice */}
      {(user?.role === 'SUPERKASIR' || user?.role === 'OWNER' || user?.role === 'MANAGER') && (
        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center gap-2 text-indigo-800">
            <CheckCircle size={20} />
            <span className="font-semibold">
              {user?.role === 'SUPERKASIR' ? 'Mode Superkasir:' :
               user?.role === 'OWNER' ? 'Mode Owner:' :
               'Mode Manager:'}
            </span>
          </div>
          <p className="text-indigo-700 mt-1">
            Anda melihat semua laporan shift dari seluruh loket.
            {user?.role === 'SUPERKASIR' && ' Anda hanya bisa melihat detail, tidak bisa edit atau hapus.'}
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Filter by Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={16} className="inline mr-1" />
              Filter Tanggal
            </label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                loadRiwayatLaporan();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Search size={16} className="inline mr-1" />
              Cari Laporan
            </label>
            <input
              type="text"
              placeholder="Cari nomor laporan, shift, atau loket..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <AlertCircle size={20} className="mr-2" />
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <CheckCircle size={20} className="mr-2" />
          {success}
        </div>
      )}

      {/* No Data */}
      {!loading && filteredLaporan.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText size={64} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            {searchTerm || filterDate ? 'Tidak ada laporan ditemukan' : 'Belum ada riwayat laporan'}
          </h3>
          <p className="text-gray-500">
            {searchTerm || filterDate
              ? 'Coba ubah filter atau kata kunci pencarian'
              : 'Laporan shift yang Anda buat akan muncul di sini'}
          </p>
        </div>
      )}

      {/* Laporan List */}
      {filteredLaporan.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedLaporan.map((laporan) => (
            <div
              key={laporan.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-4 cursor-pointer"
              onClick={() => viewDetail(laporan)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-800">{laporan.nomor_laporan}</h3>
                  <p className="text-sm text-gray-500">{formatDate(laporan.created_at)}</p>
                  <p className="text-xs text-indigo-600 font-medium">{getNamaHari(laporan.created_at)}</p>
                  {/* Show loket name for SUPERKASIR/OWNER/MANAGER */}
                  {(user?.role === 'SUPERKASIR' || user?.role === 'OWNER' || user?.role === 'MANAGER') && laporan.loket_nama && (
                    <p className="text-xs text-indigo-600 font-medium mt-1">
                      📍 {laporan.loket_nama}
                    </p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  laporan.shift_period === 'PAGI' ? 'bg-green-100 text-green-800' :
                  laporan.shift_period === 'SIANG' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-indigo-100 text-indigo-800'
                }`}>
                  {laporan.shift_period}
                </span>
              </div>

              {/* Summary */}
              <div className="space-y-2 text-sm mb-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Transaksi:</span>
                  <span className="font-semibold">{laporan.total_transaksi || 0} trx</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Tagihan:</span>
                  <span className="font-semibold">
                    {formatRupiah(laporan.total_tagihan || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Admin:</span>
                  <span className="font-semibold text-blue-600">
                    {formatRupiah(laporan.total_admin_fee || 0)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-700 font-medium">Total Omzet:</span>
                  <span className="font-bold text-green-600">
                    {formatRupiah(laporan.total_omzet || 0)}
                  </span>
                </div>
              </div>

              {/* Transaksi Breakdown per Provider */}
              {laporan.bank_summaries && laporan.bank_summaries.length > 0 && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Detail Transaksi:</h4>
                  <div className="space-y-1.5 text-xs">
                    {laporan.bank_summaries.map((bank) =>
                      bank.transaksi_list && bank.transaksi_list.map((trx, idx) => (
                        <div key={`${bank.bank_id}-${idx}`} className="flex justify-between items-center">
                          <span className="text-gray-600">
                            {trx.nama_provider || trx.provider_name}
                            <span className="text-gray-400 ml-1">({trx.tipe_transaksi || 'TUNAI'})</span>
                          </span>
                          <span className="font-medium">
                            {trx.jumlah_trx || trx.jumlah_transaksi || 0} trx
                            <span className="text-blue-600 ml-1">
                              @{formatRupiah(trx.biaya_admin_per_trx || 0)}/trx
                            </span>
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-3 pt-3 border-t flex gap-2 flex-wrap">
                {/* Upload Bukti - MULTIPLE FILES */}
                <label
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                    laporan.bukti_urls && laporan.bukti_urls.length > 0
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Upload size={16} />
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        if (window.confirm(`Upload ${files.length} bukti transaksi?`)) {
                          handleUploadBukti(files, laporan.id);
                        }
                      }
                    }}
                    disabled={uploading}
                  />
                  {laporan.bukti_urls && laporan.bukti_urls.length > 0
                    ? `Update Bukti (${laporan.bukti_urls.length})`
                    : 'Upload Bukti'}
                </label>

                {/* View Detail */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    viewDetail(laporan);
                  }}
                  className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                >
                  <Eye size={16} />
                  Detail
                </button>

                {/* Share WhatsApp */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    shareToWhatsApp(laporan);
                  }}
                  className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                  title="Share ke WhatsApp"
                >
                  <MessageCircle size={16} />
                  WhatsApp
                </button>

                {/* Edit Button - Only if allowed */}
                {settings.allow_edit_laporan && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(laporan);
                    }}
                    className="flex items-center gap-1 px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-200 transition-colors"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                )}

                {/* Delete Button - Only if allowed */}
                {settings.allow_delete_laporan && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLaporan(laporan.id);
                    }}
                    className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                  >
                    <Trash2 size={16} />
                    Hapus
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow p-4">
            {/* Info */}
            <div className="text-sm text-gray-600">
              Menampilkan {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredLaporan.length)} dari {filteredLaporan.length} laporan
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1">
              {/* Previous */}
              <button
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
                className={`p-2 rounded-lg ${
                  safePage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronLeft size={18} />
              </button>

              {/* Page Numbers */}
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`w-10 h-10 rounded-lg font-medium ${
                      page === safePage
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                )
              ))}

              {/* Next */}
              <button
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
                className={`p-2 rounded-lg ${
                  safePage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </>
      )}

      {/* Edit Modal */}
      {showEditModal && editingLaporan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit Laporan Shift</h2>
                <p className="text-sm text-gray-500">{editingLaporan.nomor_laporan}</p>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingLaporan(null);
                  setBankTransaksi({ BSI: {}, MANDIRI: {} });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleUpdateSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Tanggal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tanggal
                  </label>
                  <input
                    type="date"
                    value={editingLaporan.tanggal || ''}
                    onChange={(e) => setEditingLaporan({...editingLaporan, tanggal: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Shift Period */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shift
                  </label>
                  <select
                    value={editingLaporan.shift_period || ''}
                    onChange={(e) => setEditingLaporan({...editingLaporan, shift_period: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="PAGI">Pagi</option>
                    <option value="SIANG">Siang</option>
                    <option value="SORE">Sore</option>
                    <option value="MALAM">Malam</option>
                  </select>
                </div>

                {/* Saldo Awal BSI */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Saldo Awal BSI
                  </label>
                  <input
                    type="number"
                    value={editingLaporan.saldo_awal_bsi || 0}
                    onChange={(e) => setEditingLaporan({...editingLaporan, saldo_awal_bsi: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Saldo Awal Mandiri */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Saldo Awal Mandiri
                  </label>
                  <input
                    type="number"
                    value={editingLaporan.saldo_awal_mandiri || 0}
                    onChange={(e) => setEditingLaporan({...editingLaporan, saldo_awal_mandiri: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Editable Transactions */}
              <div className="mt-6">
                <h3 className="font-semibold text-lg mb-3">Transaksi PPOB</h3>

                {/* Bank Tabs */}
                <div className="flex gap-2 mb-4 border-b">
                  <button
                    type="button"
                    onClick={() => setActiveEditTab('BSI')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeEditTab === 'BSI'
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    BSI (Bank Syariah Indonesia)
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveEditTab('MANDIRI')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeEditTab === 'MANDIRI'
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Bank Mandiri
                  </button>
                </div>

                {/* Provider List for Active Bank */}
                <div className="space-y-4">
                  {providers.map((provider) => {
                    const providerData = bankTransaksi[activeEditTab]?.[provider.id];
                    if (!providerData) return null;

                    const tunaiData = providerData.tunai || { checked: false, jumlah: 0, tagihan: 0, admin_fee: 0 };
                    const nonTunaiData = providerData.non_tunai || { checked: false, jumlah: 0, tagihan: 0, admin_fee: 0 };

                    return (
                      <div key={provider.id} className="border rounded-lg p-4">
                        {/* Provider Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{provider.nama}</h4>
                            <p className="text-sm text-gray-500">{provider.kategori}</p>
                          </div>
                          <div className="text-sm text-gray-600">
                            Admin Fee: Rp {providerData.biaya_admin || 0}/trx
                          </div>
                        </div>

                        {/* TUNAI Section */}
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <input
                              type="checkbox"
                              id={`tunai-${provider.id}`}
                              checked={tunaiData.checked}
                              onChange={() => toggleTransactionType(activeEditTab, provider.id, 'tunai')}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <label htmlFor={`tunai-${provider.id}`} className="font-medium text-gray-700">
                              TUNAI
                            </label>
                          </div>

                          {tunaiData.checked && (
                            <div className="grid grid-cols-2 gap-3 mt-2">
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Jumlah Transaksi</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={tunaiData.jumlah}
                                  onChange={(e) => updateTransaksi(activeEditTab, provider.id, 'tunai', 'jumlah', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Total Tagihan</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="1000"
                                  value={tunaiData.tagihan}
                                  onChange={(e) => updateTransaksi(activeEditTab, provider.id, 'tunai', 'tagihan', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          )}

                          {tunaiData.checked && tunaiData.jumlah > 0 && (
                            <div className="mt-2 text-sm text-gray-600">
                              Admin Fee: {formatRupiah(tunaiData.admin_fee)}
                            </div>
                          )}
                        </div>

                        {/* NON-TUNAI Section */}
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <input
                              type="checkbox"
                              id={`non-tunai-${provider.id}`}
                              checked={nonTunaiData.checked}
                              onChange={() => toggleTransactionType(activeEditTab, provider.id, 'non_tunai')}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <label htmlFor={`non-tunai-${provider.id}`} className="font-medium text-gray-700">
                              NON-TUNAI
                            </label>
                          </div>

                          {nonTunaiData.checked && (
                            <div className="grid grid-cols-2 gap-3 mt-2">
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Jumlah Transaksi</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={nonTunaiData.jumlah}
                                  onChange={(e) => updateTransaksi(activeEditTab, provider.id, 'non_tunai', 'jumlah', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Total Tagihan</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="1000"
                                  value={nonTunaiData.tagihan}
                                  onChange={(e) => updateTransaksi(activeEditTab, provider.id, 'non_tunai', 'tagihan', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          )}

                          {nonTunaiData.checked && nonTunaiData.jumlah > 0 && (
                            <div className="mt-2 text-sm text-gray-600">
                              Admin Fee: {formatRupiah(nonTunaiData.admin_fee)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {providers.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Tidak ada provider tersedia
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingLaporan(null);
                    setBankTransaksi({ BSI: {}, MANDIRI: {} });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Simpan Perubahan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLaporan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Detail Laporan Shift</h2>
                <p className="text-sm text-gray-600">{selectedLaporan.nomor_laporan}</p>
              </div>
              <button
                onClick={closeDetail}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Share WhatsApp Button */}
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => shareToWhatsApp(selectedLaporan)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <MessageCircle size={18} />
                  Share ke WhatsApp
                </button>
              </div>

              {/* Info Umum */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Tanggal</p>
                  <p className="font-semibold">{formatDate(selectedLaporan.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Shift</p>
                  <p className="font-semibold">{selectedLaporan.shift_period}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Loket</p>
                  <p className="font-semibold">{selectedLaporan.loket_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Petugas</p>
                  <p className="font-semibold">{selectedLaporan.created_by_name || '-'}</p>
                </div>
              </div>

              {/* Ringkasan Per Bank */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-3">Ringkasan Per Bank</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedLaporan.bank_summaries?.map((bank, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${
                      bank.bank_id === 'BSI' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'
                    }`}>
                      <h4 className="font-semibold mb-2">{bank.bank_nama}</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Saldo Awal:</span>
                          <span className="font-medium">{formatRupiah(bank.saldo_awal || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Transaksi:</span>
                          <span className="font-medium">{bank.total_transaksi || 0} trx</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Tagihan:</span>
                          <span className="font-medium">{formatRupiah(bank.total_tagihan || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Admin:</span>
                          <span className="font-medium text-blue-600">{formatRupiah(bank.total_admin_fee || 0)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-1 mt-1">
                          <span className="font-semibold">Saldo Akhir:</span>
                          <span className="font-bold">{formatRupiah(bank.saldo_akhir || 0)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Uang Kecil / Sisa Kembalian */}
              {selectedLaporan.uang_kecil !== undefined && selectedLaporan.uang_kecil !== null && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-yellow-800">💰 Uang Kecil / Sisa Kembalian</h4>
                      <p className="text-sm text-yellow-700">Uang sisa dari transaksi yang ditahan di loket</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-yellow-900">{formatRupiah(selectedLaporan.uang_kecil || 0)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Transaksi Per Provider */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Transaksi Per Provider</h3>
                <div className="space-y-3">
                  {selectedLaporan.bank_summaries?.map((bank) =>
                    bank.transaksi_list?.map((trx, idx) => (
                      <div key={`${bank.bank_id}-${idx}`} className="bg-gray-50 rounded p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{trx.nama_provider || trx.provider_name}</p>
                            <p className="text-sm text-gray-600">{trx.tipe_transaksi || 'TUNAI'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{trx.jumlah_trx || trx.jumlah_transaksi || 0} trx</p>
                            <p className="text-sm text-gray-600">{formatRupiah((trx.total_nominal || 0) + (trx.total_admin_fee || 0))}</p>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600">
                          <div>Tagihan: {formatRupiah(trx.total_nominal || 0)}</div>
                          <div className="text-blue-600 font-medium">Admin: {formatRupiah(trx.total_admin_fee || 0)}</div>
                          <div>Biaya/trx: {formatRupiah(trx.biaya_admin_per_trx || 0)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Bukti Transaksi */}
              <div className="mt-6">
                <h3 className="font-semibold text-lg mb-3">📎 Bukti Transaksi</h3>
                {selectedLaporan.bukti_urls && selectedLaporan.bukti_urls.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedLaporan.bukti_urls.map((url, idx) => (
                      <div key={idx} className="group relative">
                        <a
                          href={`${api}${url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={`${api}${url}`}
                            alt={`Bukti ${idx + 1}`}
                            className="w-full h-40 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-all cursor-pointer"
                          />
                        </a>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                          <Eye size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center">Klik untuk perbesar</p>

                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBukti(selectedLaporan.id, url);
                          }}
                          disabled={uploading}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Hapus bukti"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <FileText size={48} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500">Belum ada bukti transaksi yang diupload</p>
                  </div>
                )}
              </div>

              {/* Catatan */}
              {selectedLaporan.catatan && (
                <div className="mt-6 p-4 bg-gray-50 rounded">
                  <h4 className="font-medium mb-1">Catatan:</h4>
                  <p className="text-sm text-gray-700">{selectedLaporan.catatan}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiwayatLoketPage;
